import { Link } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { Countdown } from '../components/Countdown';
import { InfoRows } from '../components/InfoRows';
import { EarningsScale } from '../components/EarningsScale';
import { MapCard } from '../components/MapCard';
import { StepList } from '../components/StepList';
import { PointsPill } from '../components/PointsPill';
import { PrimaryAction } from '../components/PrimaryAction';
import { ErrorState, Loading } from '../components/StateBlock';
import { useAsync } from '../hooks/useAsync';
import { getChallenge } from '../api';
import { paths } from '../paths';
import { fmtDuration } from '../utils/format';

function ImportantNote({ className = '' }: { className?: string }) {
  return (
    <aside className={`pv-note ${className}`}>
      <Icon name="info" size={22} />
      <div>
        <strong>Informação importante</strong>
        <p>
          O QR Code vale até o fim do horário do desafio e só pode ser lido no
          local do desafio.
        </p>
      </div>
    </aside>
  );
}

/** Detail of a presence challenge: countdown, date, place, points and how it works. */
export function ChallengeDetailPage({ eventId }: { eventId: string }) {
  const {
    data: challenge,
    loading,
    error,
    reload,
  } = useAsync(() => getChallenge(eventId), [eventId]);

  if (loading && !challenge) return <Loading />;
  if (error || !challenge) {
    return <ErrorState message="Desafio não encontrado." onRetry={reload} />;
  }

  const { rules, attendance } = challenge;
  const status = attendance?.status;

  return (
    <div className="pv-container pv-detail-page">
      <Link to={paths.list()} className="pv-back">
        <Icon name="back" size={18} /> Desafios para validar
      </Link>

      <div className="pv-detail">
        <div className="pv-detail__head pv-stack">
          <div
            className="pv-banner"
            style={
              challenge.bannerUrl
                ? { backgroundImage: `url(${challenge.bannerUrl})` }
                : undefined
            }
            role={challenge.bannerUrl ? 'img' : undefined}
            aria-label={challenge.bannerUrl ? challenge.title : undefined}
          />
          <div className="pv-stack pv-stack--sm">
            <div className="pv-chips">
              <span className="pv-pill pv-pill--tint">Presencial</span>
              <PointsPill points={rules.maxPoints} prefix="+" />
            </div>
            <h1 className="pv-h1">{challenge.title}</h1>
            {challenge.description && (
              <p className="pv-muted">{challenge.description}</p>
            )}
          </div>
        </div>

        <div className="pv-detail__aside pv-stack">
          <section
            className="pv-card pv-stack"
            aria-label="Situação do desafio"
          >
            {status === 'CHECKED_IN' ? (
              <span className="pv-pill pv-pill--success-solid">
                Você está no local
              </span>
            ) : status === 'COMPLETED' ? (
              <span className="pv-pill pv-pill--success-solid">
                Desafio concluído
              </span>
            ) : status === 'MISSED' ? (
              <span className="pv-pill pv-pill--tint">Desafio encerrado</span>
            ) : (
              <>
                <span className="pv-muted pv-small">Começa em</span>
                <Countdown target={challenge.startsAt} />
              </>
            )}

            <div className="pv-aside-points">
              <span className="pv-muted">Você ganha</span>
              <strong>até {rules.maxPoints} pts</strong>
            </div>

            <PrimaryAction
              challenge={challenge}
              className="pv-btn--block pv-hide-mobile"
            />
          </section>

          <ImportantNote className="pv-hide-mobile" />
        </div>

        <div className="pv-detail__rest pv-stack">
          <InfoRows challenge={challenge} />
          <EarningsScale rules={rules} />
          <MapCard location={challenge.location} />
          <StepList rules={rules} />
          {challenge.regulation && (
            <section className="pv-card pv-stack pv-stack--sm">
              <h2 className="pv-h3">Regras</h2>
              <p className="pv-muted" style={{ whiteSpace: 'pre-line' }}>
                {challenge.regulation}
              </p>
            </section>
          )}
          {challenge.moreDetails && (
            <section className="pv-card pv-stack pv-stack--sm">
              <h2 className="pv-h3">Benefícios</h2>
              <p className="pv-muted" style={{ whiteSpace: 'pre-line' }}>
                {challenge.moreDetails}
              </p>
            </section>
          )}
          <ImportantNote className="pv-hide-desktop" />
        </div>
      </div>

      <div className="pv-cta-bar">
        <div>
          {rules.minStayMinutes ? (
            <div className="pv-muted pv-small">
              Mínimo {fmtDuration(rules.minStayMinutes)}
            </div>
          ) : null}
          <strong className="pv-cta-bar__points">
            até {rules.maxPoints} pts
          </strong>
        </div>
        <PrimaryAction challenge={challenge} className="pv-btn--grow" />
      </div>
    </div>
  );
}
